import { API_BASE_URL, endpoints } from '../config/api';
import { getSupabaseClient, BUSINESS_TYPES } from '../supabaseClient';

/**
 * Fetch conflicts directly from the brain_conflicts table
 * Gets the most recent analysis for the current user
 */
export const fetchConflictsFromDatabase = async (supabase) => {
  try {
    // Get current user for auth
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) {
      return [];
    }

    // Get the most recent analysis timestamp for this user
    const { data: latestAnalysis, error: timestampError } = await supabase
      .from('brain_conflicts')
      .select('analysis_metadata')
      .eq('account_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1);

    if (timestampError) {
      return [];
    }

    if (!latestAnalysis || latestAnalysis.length === 0) {
      return []; // No conflicts found
    }

    const latestTimestamp = latestAnalysis[0].analysis_metadata?.analysis_timestamp;
    if (!latestTimestamp) {
      return []; // No valid timestamp found
    }

    // Fetch all conflicts from the most recent analysis
    const { data: conflicts, error: conflictsError } = await supabase
      .from('brain_conflicts')
      .select('*')
      .eq('account_id', user.id)
      .eq('analysis_metadata->>analysis_timestamp', latestTimestamp)
      .order('priority', { ascending: true }); // Sort by priority (1, 2, 3)

    if (conflictsError) {
      return [];
    }

    return conflicts || [];
  } catch (error) {
    return [];
  }
};

/**
 * Automatically detect conflicts after settings are saved
 * First triggers the API to run analysis and save to database, then fetches from database
 */
export const runAutomaticConflictDetection = async (supabase, onConflictsUpdated = null) => {
  try {
    // Get current user for auth
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) {
      return [];
    }

    // First, trigger the conflict detection API to analyze and save to database
    const response = await fetch(`${API_BASE_URL}${endpoints.conflictDetection}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.access_token}`,
      },
      body: JSON.stringify({
        account_id: user.id
      })
    });

    if (!response.ok) {
      // Still try to fetch existing conflicts from database
    }

    // Now fetch the latest conflicts from the database
    const conflicts = await fetchConflictsFromDatabase(supabase);

    // Update Brain component state if callback provided
    if (onConflictsUpdated && typeof onConflictsUpdated === 'function') {
      onConflictsUpdated(conflicts);
    }

    // Note: Conflicts are displayed via the shield icon in PageHeader across all pages
    // No need for redundant toast notifications

    return conflicts;
  } catch (error) {
    // Silently fail - don't disrupt the user's workflow
    return [];
  }
};

/**
 * Fetch conflicts without triggering new analysis
 * Useful for page loads and checking existing conflicts
 */
export const getExistingConflicts = async (supabase) => {
  return await fetchConflictsFromDatabase(supabase);
};

/**
 * Get display label for conflict type
 */
export const getConflictTypeLabel = (conflictType) => {
  const conflictTypeLabels = {
    "contradictory_facts": "Conflicting Information",
    "ambiguous_decision": "Unclear Response", 
    "identical_purpose": "Duplicate Purpose",
    "confusing_overlap": "Customer Confusion"
  };

  return conflictTypeLabels[conflictType] || "Unknown Issue";
};

/**
 * Get confidence display text
 */
export const getConfidenceDisplay = (confidence) => {
  if (confidence >= 0.95) {
    return "Very High Confidence";
  } else if (confidence >= 0.85) {
    return "High Confidence";
  } else {
    return "Moderate Confidence";
  }
};

/**
 * Get priority display text
 */
export const getPriorityDisplay = (priority) => {
  switch (priority) {
    case 1:
      return "Critical";
    case 2:
      return "High Impact";
    case 3:
      return "Moderate";
    default:
      return "Unknown Priority";
  }
}; 